"use client"

import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronDown } from "lucide-react"
import { useRouter } from "next/navigation"
import { Navbar } from "@/components/layout/navbar"
import { Footer } from "@/components/layout/footer"

interface ThinkingStyle {
  name: string
  description: string
  signature: string
}

interface Family {
  name: string
  tagline: string
  styles: ThinkingStyle[]
}

const families: Family[] = [
  {
    name: "Depth-Oriented",
    tagline: "Goes deep. Stays deep.",
    styles: [
      {
        name: "The Diver",
        description:
          "Goes to the bottom of everything. Where others skim the surface, you're already three levels down.",
        signature: "Low topic diversity · High engagement depth · Long messages",
      },
      {
        name: "The Perfectionist",
        description:
          "Won't ship it until every detail is right. Rich vocabulary, minimal questions, maximum clarity.",
        signature:
          "Low topic diversity · High vocabulary richness · Few questions asked",
      },
      {
        name: "The Monk",
        description:
          "Returns to the same ground, month after month. Depth through devotion, not novelty.",
        signature:
          "Low topic diversity · Low attention entropy · High temporal spread",
      },
    ],
  },
  {
    name: "Breadth-Oriented",
    tagline: "Sees the whole board.",
    styles: [
      {
        name: "The Polymath",
        description:
          "Interested in everything. Good at more of it than you should be.",
        signature:
          "High topic diversity · High attention entropy · Rich vocabulary",
      },
      {
        name: "The Connector",
        description:
          "Sees the thread between things that don't seem related. Breadth in service of synthesis.",
        signature:
          "High topic diversity · Moderate entropy · Deep engagement",
      },
      {
        name: "The Wanderer",
        description:
          "Follows whatever's interesting. Ends up somewhere surprising.",
        signature:
          "High topic diversity · High question ratio · Moderate message length",
      },
    ],
  },
  {
    name: "Interrogative",
    tagline: "The question is the point.",
    styles: [
      {
        name: "The Archaeologist",
        description:
          "Digs for what's buried. Keeps going until you find it.",
        signature:
          "High question ratio · Moderate topic diversity · Deep engagement",
      },
      {
        name: "The Sparring Partner",
        description:
          "Thinks by bouncing off someone else. The conversation IS the thinking.",
        signature:
          "High question ratio · Many conversations · Moderate message length",
      },
      {
        name: "The Questioner",
        description:
          "Asks the question nobody else thought to ask. Then asks it again differently.",
        signature: "Very high question ratio · High topic diversity",
      },
    ],
  },
  {
    name: "Elaborative",
    tagline: "Thinks in paragraphs, not sentences.",
    styles: [
      {
        name: "The Systems Thinker",
        description:
          "Sees how everything connects. Builds the model before building the thing.",
        signature:
          "High message length · Rich vocabulary · Low question ratio",
      },
      {
        name: "The Storyteller",
        description:
          "Makes sense of things by telling the story of them.",
        signature:
          "Very high message length · Moderate topic range · High temporal spread",
      },
      {
        name: "The Theorist",
        description:
          "Needs a framework before they can act. Builds theories the way others build furniture.",
        signature:
          "High message length · Rich vocabulary · Low attention entropy",
      },
    ],
  },
  {
    name: "Sustained Engagement",
    tagline: "Shows up. Keeps showing up.",
    styles: [
      {
        name: "The Student",
        description:
          "Always learning. Treats everything as curriculum.",
        signature:
          "Many conversations · High temporal spread · Moderate depth",
      },
      {
        name: "The Refiner",
        description:
          "First draft is never the point. The polishing IS the thinking.",
        signature:
          "Very high conversation count · High temporal spread · Moderate-high message length",
      },
    ],
  },
  {
    name: "Conversational",
    tagline: "Thinking is dialogue.",
    styles: [
      {
        name: "The Co-Pilot",
        description:
          "Thinks best alongside someone else. The partnership makes both better.",
        signature:
          "High engagement depth · Many conversations · High question ratio",
      },
      {
        name: "The Bridge Builder",
        description:
          "Connects worlds that don't normally talk to each other.",
        signature:
          "High engagement depth · High topic diversity · Rich vocabulary",
      },
    ],
  },
]

function FamilySection({ family, index }: { family: Family; index: number }) {
  const [expanded, setExpanded] = useState<number | null>(null)

  return (
    <motion.div
      className="mb-16"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 + index * 0.06 }}
    >
      <div className="mb-6">
        <h3 className="font-[var(--font-instrument-serif)] text-2xl font-normal text-[var(--text-primary)]">
          {family.name}
        </h3>
        <p className="mt-1 text-sm italic text-[var(--text-muted)]">
          &ldquo;{family.tagline}&rdquo;
        </p>
      </div>

      <div className="space-y-3">
        {family.styles.map((style, i) => {
          const isOpen = expanded === i
          return (
            <div
              key={style.name}
              className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-white shadow-[var(--shadow-card)] overflow-hidden transition-shadow hover:shadow-[var(--shadow-elevated)]"
            >
              <button
                onClick={() => setExpanded(isOpen ? null : i)}
                className="flex w-full items-center justify-between px-6 py-4 text-left"
              >
                <span className="font-semibold text-[var(--text-primary)]">
                  {style.name}
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-[var(--text-muted)] transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-5">
                      <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
                        {style.description}
                      </p>
                      <p className="mt-3 text-xs font-medium text-[var(--text-muted)]">
                        Signature: {style.signature}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )
        })}
      </div>
    </motion.div>
  )
}

export default function PatternsPage() {
  const router = useRouter()

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-[var(--bg-page)] pt-28 pb-20">
        <div className="mx-auto max-w-3xl px-6">
          {/* Section 1: Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-[var(--font-instrument-serif)] text-4xl font-normal text-[var(--text-primary)] sm:text-5xl">
              16 Thinking Styles
            </h1>
            <p className="mt-3 font-[var(--font-instrument-serif)] text-2xl text-[var(--text-secondary)]">
              Which one are you?
            </p>
            <p className="mt-6 text-base leading-relaxed text-[var(--text-secondary)]">
              Your conversations reveal how you think &mdash; the depth of your
              focus, the breadth of your curiosity, the pace of your evolution.
              We&apos;ve mapped 16 distinct patterns.
            </p>
          </motion.div>

          {/* Section 2: The 6 Families */}
          <div className="mt-16">
            {families.map((family, i) => (
              <FamilySection key={family.name} family={family} index={i} />
            ))}
          </div>

          {/* Section 3: CTA */}
          <motion.div
            className="mt-8 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="divider-gold mx-auto mb-16 max-w-xs" />

            <h2 className="font-[var(--font-instrument-serif)] text-3xl font-normal leading-snug text-[var(--text-primary)] sm:text-4xl">
              You already know which one you think you are.
              <br />
              Find out which one you actually are.
            </h2>

            <button
              onClick={() => router.push("/upload")}
              className="btn-gold mt-10 inline-block rounded-full px-10 py-4 text-lg"
            >
              Discover Your Thinking Style &mdash; Free
            </button>

            <p className="mt-5 text-sm text-[var(--text-muted)]">
              Upload your AI conversations &middot; Instant preview &middot; No
              account needed
            </p>
          </motion.div>
        </div>
      </main>
      <Footer />
    </>
  )
}
